/**
 * Poly Messenger React Hooks
 *
 * Extends @estream/sdk-browser/react with Poly-specific hooks.
 *
 * This module re-exports all estream React functionality and adds
 * Poly-specific data hooks that use estream's useSubscription under the hood.
 */
// Re-export everything from @estream/sdk-browser/react
export { EstreamProvider, useEstreamClient, useSubscription, useEmit, useConnection, useCircuit, useSpark, getStoredSession, } from '@estream/sdk-browser/react';
// Re-export estream Spark utilities
export { SPARK_TOPICS, SPARK_TTL, CONSOLE_ACTIONS, createConsoleLoginPayload, generateChallengeId, generateSessionId, generateNonce, } from '@estream/sdk-browser/react';
// Poly actions
export { POLY_ACTIONS, getActionThreshold } from './actions.js';
// ============================================================================
// Poly Lattice Paths
// ============================================================================
/**
 * Poly-specific lattice paths for console data
 */
export const POLY_LATTICE = {
    // Device management
    DEVICES: 'console.devices',
    DEVICES_SNAPSHOT: 'console.devices.snapshot',
    DEVICES_DELTA: 'console.devices.delta',
    DEVICES_PENDING: 'console.devices.pending',
    // Platform connections
    PLATFORMS: 'console.platforms',
    PLATFORMS_CONNECTED: 'console.platforms.connected',
    // Messages
    MESSAGES: 'console.messages',
    MESSAGES_INBOX: 'console.messages.inbox',
    // Governance
    GOVERNANCE: 'console.governance',
    GOVERNANCE_PENDING: 'console.governance.pending',
    GOVERNANCE_AUDIT: 'console.governance.audit',
    // Activity
    ACTIVITY: 'console.activity',
    ACTIVITY_RECENT: 'console.activity.recent',
};
// ============================================================================
// Poly Data Hooks
// ============================================================================
// Import useSubscription for internal use
import { useSubscription } from '@estream/sdk-browser/react';
/**
 * Hook for subscribing to device list
 *
 * Uses ESLite snapshot+delta pattern for efficient updates.
 *
 * @example
 * ```tsx
 * function DeviceList() {
 *   const { data: devices, loading, error, refresh } = useDevices();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return (
 *     <ul>
 *       {devices.map(d => <li key={d.id}>{d.name}</li>)}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useDevices() {
    return useSubscription(POLY_LATTICE.DEVICES_SNAPSHOT);
}
/**
 * Hook for subscribing to pending device link requests
 */
export function usePendingDevices() {
    return useSubscription(POLY_LATTICE.DEVICES_PENDING);
}
/**
 * Hook for subscribing to connected platforms
 */
export function usePlatforms() {
    return useSubscription(POLY_LATTICE.PLATFORMS_CONNECTED);
}
/**
 * Hook for subscribing to governance data
 */
export function useGovernance() {
    return useSubscription(POLY_LATTICE.GOVERNANCE_PENDING);
}
/**
 * Hook for subscribing to message inbox
 */
export function useMessages() {
    return useSubscription(POLY_LATTICE.MESSAGES_INBOX);
}
/**
 * Hook for subscribing to recent activity
 */
export function useActivity() {
    return useSubscription(POLY_LATTICE.ACTIVITY_RECENT);
}
// ============================================================================
// Poly Session Utilities
// ============================================================================
const POLY_SESSION_KEY = 'poly_session';
/**
 * Get stored Poly session from localStorage
 *
 * This is a Poly-specific wrapper that provides Poly session structure.
 * For general estream sessions, use getStoredSession from @estream/sdk-browser.
 */
export function getPolySession() {
    if (typeof localStorage === 'undefined')
        return null;
    try {
        const stored = localStorage.getItem(POLY_SESSION_KEY);
        if (!stored)
            return null;
        const session = JSON.parse(stored);
        // Check if expired
        if (session.expiresAt < Date.now()) {
            localStorage.removeItem(POLY_SESSION_KEY);
            return null;
        }
        return session;
    }
    catch {
        return null;
    }
}
/**
 * Store Poly session
 */
export function setPolySession(session) {
    if (typeof localStorage === 'undefined')
        return;
    localStorage.setItem(POLY_SESSION_KEY, JSON.stringify(session));
}
/**
 * Clear Poly session
 */
export function clearPolySession() {
    if (typeof localStorage === 'undefined')
        return;
    localStorage.removeItem(POLY_SESSION_KEY);
}
//# sourceMappingURL=react.js.map